import type { AudioPlayer } from "@yetric/audioplayer";

const SEEK_OFFSET_SECONDS = 10;
/** The cover is the same at every size; one entry is enough and one fetch per episode */
const ARTWORK_SIZE = 480;
/** Playback that drifts this far from where the system was told it is has been seeked */
const POSITION_DRIFT_SECONDS = 2;

type SessionPlayer = Pick<AudioPlayer, "getState" | "subscribe" | "play" | "pause" | "seek">;

const hasMediaSession = () => typeof navigator !== "undefined" && "mediaSession" in navigator;

/**
 * Lets the lock screen, headphones and the browser's media controls show and drive the episode that is playing.
 *
 * The metadata is only replaced when the episode changes. Setting it on every player update (about once a
 * second while playing) makes the browser fetch the cover again each time, so the position is the only thing
 * that is kept up to date, and only when it has jumped or the playback state has changed.
 */
export const attachMediaSession = (player: SessionPlayer) => {
    if (!hasMediaSession()) {
        return { destroy() {} };
    }

    const session = navigator.mediaSession;
    let sourceId: string | null = null;
    let status = "";
    let rate = 0;
    let duration = 0;
    let reportedAt = { position: 0, time: 0 };

    const sync = () => {
        const state = player.getState();
        const source = state.currentSource;

        if ((source?.id ?? null) !== sourceId) {
            sourceId = source?.id ?? null;
            session.metadata = source
                ? new MediaMetadata({
                      title: source.title ?? "",
                      artist: source.artist ?? "",
                      album: source.album ?? "",
                      artwork: source.artwork
                          ? [{ src: source.artwork, sizes: `${ARTWORK_SIZE}x${ARTWORK_SIZE}` }]
                          : [],
                  })
                : null;
        }

        session.playbackState = state.status === "playing" ? "playing" : source ? "paused" : "none";

        // Where the position should be by now if nothing has happened since it was last reported
        const expected =
            reportedAt.position +
            (state.status === "playing" ? ((Date.now() - reportedAt.time) / 1000) * state.rate : 0);
        const changed =
            state.status !== status ||
            state.rate !== rate ||
            state.duration !== duration ||
            Math.abs(state.currentTime - expected) > POSITION_DRIFT_SECONDS;
        if (!changed) {
            return;
        }

        status = state.status;
        rate = state.rate;
        duration = state.duration;
        reportedAt = { position: state.currentTime, time: Date.now() };
        try {
            if (source && state.duration > 0) {
                session.setPositionState({
                    duration: state.duration,
                    playbackRate: state.rate,
                    position: Math.min(state.currentTime, state.duration),
                });
            } else {
                session.setPositionState();
            }
        } catch {
            // Some browsers refuse a position they find odd
        }
    };

    const handlers: Array<[MediaSessionAction, MediaSessionActionHandler | null]> = [
        ["play", () => void player.play()],
        ["pause", () => player.pause()],
        ["seekbackward", () => player.seek(Math.max(0, player.getState().currentTime - SEEK_OFFSET_SECONDS))],
        ["seekforward", () => player.seek(player.getState().currentTime + SEEK_OFFSET_SECONDS)],
        [
            "seekto",
            (details) => {
                if (details.seekTime !== undefined && details.seekTime !== null) player.seek(details.seekTime);
            },
        ],
        ["stop", () => player.pause()],
    ];
    const setHandler = (action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
        try {
            session.setActionHandler(action, handler);
        } catch {
            // An action this browser does not know
        }
    };
    handlers.forEach(([action, handler]) => setHandler(action, handler));

    const unsubscribe = player.subscribe(sync);
    sync();

    return {
        destroy() {
            unsubscribe();
            handlers.forEach(([action]) => setHandler(action, null));
            session.metadata = null;
            session.playbackState = "none";
            try {
                session.setPositionState();
            } catch {
                // Nothing to clear
            }
        },
    };
};
